import { useEffect, useState } from 'react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { todasAsLinhas } from '@/components/atomic-crm/providers/supabase/todasAsLinhas';
import { formatCurrency } from '../blocos/tipos';
import type { DadosInicio } from './useDadosInicio';
import { CardBento, Rotulo, Sub, ValorGrande } from './ui';
import { useMascara } from '../../valores-ocultos/contexto';

/**
 * Carteira ativa — a caixinha que junta "Cotas ativas" e "Persistência" (duas
 * caixas do desenho antigo) desde o v3: cotas, crédito na rua e o % da
 * carteira que segue de pé, num clique que abre Vendas.
 *
 * O cálculo da persistência é o de sempre: retenção = ativo / (ativo +
 * cancelado por churn), em R$ de crédito. Cancelamento sem churn (tentativa
 * não confirmada) não entra — nunca foi cota.
 */
const PERSISTENCIA_MIN_PCT = 80;

export const CardCarteiraAtiva = ({
  data,
  isSuperAdmin,
  scopedCrmUserIds,
  viewAsCrmUserId,
  navigate,
}: {
  data: DadosInicio;
  isSuperAdmin: boolean;
  scopedCrmUserIds: number[];
  viewAsCrmUserId: number | null;
  navigate: (to: string) => void;
}) => {
  const m = useMascara();
  const [persistencia, setPersistencia] = useState<number | null>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      // Em páginas: a carteira inteira já passa das 1000 linhas do PostgREST.
      const { data: rows } = await todasAsLinhas(() =>
        supabase
          .from('servopa_vendas_with_details')
          .select('valor_credito, status, sales_person_id, cancel_kind'),
      );
      if (!ativo) return;

      const filtro =
        viewAsCrmUserId != null
          ? new Set([viewAsCrmUserId])
          : isSuperAdmin
            ? null
            : new Set(scopedCrmUserIds);

      let vivo = 0;
      let cancelado = 0;
      for (const v of (rows ?? []) as {
        valor_credito: number | null;
        status: string | null;
        sales_person_id: number | null;
        cancel_kind: string | null;
      }[]) {
        const valor = Number(v.valor_credito) || 0;
        if (!valor) continue;
        if (filtro && (v.sales_person_id == null || !filtro.has(v.sales_person_id))) continue;
        if (v.status === 'confirmed') vivo += valor;
        else if (v.status === 'cancelled' && v.cancel_kind === 'churn') cancelado += valor;
      }
      const base = vivo + cancelado;
      setPersistencia(base > 0 ? (vivo * 100) / base : 100);
    })();
    return () => {
      ativo = false;
    };
  }, [isSuperAdmin, scopedCrmUserIds, viewAsCrmUserId]);

  return (
    <CardBento>
      <button
        type="button"
        onClick={() => navigate('/vendas')}
        className="block w-full cursor-pointer text-left"
      >
        <Rotulo>Carteira ativa</Rotulo>
        <ValorGrande unidade=" cotas">{m(data.vendasAtivasCount.toLocaleString('pt-BR'))}</ValorGrande>
        <Sub>{m(formatCurrency(data.vendasAtivasValor, true))} em crédito ativo</Sub>
        <Sub>
          persistência{' '}
          {persistencia == null ? (
            '…'
          ) : (
            <span
              className={
                persistencia < PERSISTENCIA_MIN_PCT
                  ? 'font-semibold text-[#b91c1c]'
                  : 'font-semibold text-[#166534]'
              }
            >
              {m(`${persistencia.toFixed(1).replace('.', ',')}%`)}
            </span>
          )}
        </Sub>
      </button>
    </CardBento>
  );
};
